import type { CSSProperties } from "react";
import { cn } from "./cn.js";

// Author-written guidance/description fields are edited as rich text and stored as HTML.
// This module is the single trusted boundary: every value is whitelist-sanitised before it is
// ever injected into the DOM, so admin-authored markup is safe to render to end users.

// Inline + simple block formatting the toolbar can produce. Anything else is unwrapped (its text
// is kept) or — for active/embedding content — dropped entirely.
const ALLOWED_TAGS = new Set([
  "P", "BR", "DIV",
  "STRONG", "B", "EM", "I", "U", "S", "STRIKE", "DEL",
  "UL", "OL", "LI", "A", "H3", "H4", "BLOCKQUOTE", "SPAN",
]);
const DROP_TAGS = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META",
  "FORM", "INPUT", "BUTTON", "TEXTAREA", "SELECT", "SVG", "IMG", "VIDEO", "AUDIO",
]);

// A stored value counts as rich HTML only if it actually carries a tag-like token. Legacy values
// (and anything a human just typed) are plain text and rendered with their line breaks preserved.
const HTML_LIKE = /<[a-z!/][\s\S]*>/i;

export function isRichHtml(value: string | null | undefined): boolean {
  return !!value && HTML_LIKE.test(value);
}

/** True when a URL is safe to use as an anchor href — an http/https/mailto scheme. Shared by the
 *  rich-text sanitiser and the structured external-resource link editor/renderer so both apply the
 *  identical rule. */
export function isSafeHref(url: string): boolean {
  return /^(https?:|mailto:)/i.test(url.trim());
}

// Text colour / highlight are the only styling the toolbar can produce, and only on SPAN. Values
// are read from the parsed element's CSSOM (already tokenised/normalised by the browser's own CSS
// parser — never the raw attribute string) and re-checked against a strict allow-list before being
// re-emitted, so `url(...)`, `expression(...)`, extra properties, etc. can never survive.
const SAFE_COLOR = /^(#[0-9a-f]{3}|#[0-9a-f]{4}|#[0-9a-f]{6}|#[0-9a-f]{8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\))$/i;

/** Build a `style` string containing only `color`/`background-color`, each re-validated against
 *  {@link SAFE_COLOR} — or `undefined` if neither survives. */
function safeInlineStyle(el: HTMLElement): string | undefined {
  const { color, backgroundColor } = el.style;
  const parts: string[] = [];
  if (color && SAFE_COLOR.test(color)) {
    parts.push(`color: ${color}`);
  }
  if (backgroundColor && SAFE_COLOR.test(backgroundColor)) {
    parts.push(`background-color: ${backgroundColor}`);
  }
  return parts.length ? parts.join("; ") : undefined;
}

function sanitizeInto(source: Node, target: Node, doc: Document): void {
  source.childNodes.forEach((child) => {
    if (child.nodeType === 3 /* text */) {
      target.appendChild(doc.createTextNode(child.textContent ?? ""));
      return;
    }
    if (child.nodeType !== 1 /* element */) {
      return; // comments, processing instructions, etc.
    }
    const el = child as Element;
    const tag = el.tagName.toUpperCase();
    if (DROP_TAGS.has(tag)) {
      return; // drop the element and all of its contents
    }
    if (!ALLOWED_TAGS.has(tag)) {
      sanitizeInto(el, target, doc); // unwrap: keep the (sanitised) children, lose the tag
      return;
    }
    const clean = doc.createElement(tag.toLowerCase());
    if (tag === "A") {
      const href = el.getAttribute("href") ?? "";
      if (isSafeHref(href)) {
        clean.setAttribute("href", href.trim());
        clean.setAttribute("target", "_blank");
        clean.setAttribute("rel", "noopener noreferrer");
      }
    }
    if (tag === "SPAN") {
      const style = safeInlineStyle(el as HTMLElement);
      if (style) {
        clean.setAttribute("style", style);
      }
      // An @mention (KEY-592): the person's id rides on the span, and the server reads it back out of the body to
      // know who to tell. Kept only when it is exactly a GUID — the one attribute value this allow-list trusts
      // besides the re-validated colours — and styled by class, never by anything the author supplied.
      const mention = el.getAttribute("data-mention") ?? "";
      if (MENTION_ID.test(mention)) {
        clean.setAttribute("data-mention", mention.toLowerCase());
        clean.setAttribute("class", "kd-mention");
      }
    }
    sanitizeInto(el, clean, doc);
    target.appendChild(clean);
  });
}

const MENTION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The people a rich-text value @mentions (KEY-592), by user id, in order of first appearance. */
export function mentionedUserIds(html: string | null | undefined): string[] {
  if (!html || typeof DOMParser === "undefined") {
    return [];
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const ids = [...doc.querySelectorAll("span[data-mention]")]
    .map((el) => (el.getAttribute("data-mention") ?? "").toLowerCase())
    .filter((id) => MENTION_ID.test(id));
  return [...new Set(ids)];
}

/**
 * Parse → whitelist → re-serialise. Strips scripts, event handlers, and any tag or attribute not
 * on the allow-list — the sole exception is `color`/`background-color` on SPAN, re-validated via
 * {@link safeInlineStyle} — so the result is safe to inject with dangerouslySetInnerHTML.
 */
export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) {
    return "";
  }
  if (typeof DOMParser === "undefined") {
    return ""; // non-browser context (e.g. SSR/tests) — never emit unsanitised markup
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out = doc.createElement("div");
  sanitizeInto(doc.body, out, doc);
  return out.innerHTML;
}

/** Flatten any rich/legacy value to plain text — for tooltips, previews and the like. */
export function richTextToPlain(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  if (!isRichHtml(value)) {
    return value;
  }
  if (typeof DOMParser === "undefined") {
    return value;
  }
  const doc = new DOMParser().parseFromString(value, "text/html");
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
}

/** True when a value has any visible content (ignores empty paragraphs / stray <br>). */
export function richTextHasContent(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  if (!isRichHtml(value)) {
    return value.trim().length > 0;
  }
  return richTextToPlain(value).length > 0;
}

const ESCAPE: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

/** Convert any stored value into HTML for a contentEditable surface: legacy plain text is escaped
 *  and its line breaks preserved as <br>, so old content stays readable when first opened. */
export function toEditableHtml(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  if (isRichHtml(value)) {
    return sanitizeRichText(value);
  }
  return value.replace(/[&<>"']/g, (c) => ESCAPE[c]).replace(/\r?\n/g, "<br>");
}

/** Render author-written rich text (or legacy plain text) safely and formatted. */
export function RichText({
  value,
  className,
  style,
}: {
  value: string | null | undefined;
  className?: string;
  style?: CSSProperties;
}) {
  if (!richTextHasContent(value)) {
    return null;
  }
  if (!isRichHtml(value)) {
    // Legacy plain text: React escapes it; CSS keeps the line breaks.
    return (
      <div className={cn("kd-richtext", className)} style={{ whiteSpace: "pre-line", ...style }}>
        {value}
      </div>
    );
  }
  return (
    <div
      className={cn("kd-richtext", className)}
      style={style}
      dangerouslySetInnerHTML={{ __html: sanitizeRichText(value) }}
    />
  );
}
